import { optionalFeeImageUrl } from '@/lib/optionalFeeIcons'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Calendar, ChevronDown, Play, Plus, Save, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import {
  FORM_CONTROL_CLASS,
  FORM_CONTROL_FOCUS_WITHIN_CLASS,
  FORM_CONTROL_SELECT_OPEN_CLASS,
} from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EntityPicker } from '@/components/shared/EntityPicker'
import { ClientNameWithProspect } from '@/components/shared/StatusBadges'
import {
  OptionalChargesSection,
  computeChargesTotal,
  PendingAttachFiles,
  DatePicker,
  type OptionalCharge,
  type PendingAttachFilesHandle,
} from '@/components/shared'
import { LineItemsEditor } from '@/components/billing/LineItemsEditor'
import {
  ServiceItemLibraryPage,
  type SelectedServiceItem,
} from '@/components/billing/ServiceItemLibraryPage'
import { AddQuoteServiceDrawer } from '@/components/billing/QuoteManualEntryDrawers'
import { OptionalFeeDocumentDialog } from '@/components/settings/services/OptionalFeeDocumentDialog'
import { TradeDiscountEditor } from '@/components/billing/TradeDiscountEditor'
import {
  useQuote,
  useCreateQuote,
  useUpdateQuote,
  useQuoteOptionalFeeTemplates,
} from '@/hooks/useQuotes'
import { useJob } from '@/hooks/useJobs'
import { useActiveServiceCategories } from '@/hooks/useServiceCategoryOptions'
import { useBreadcrumbLabel } from '@/contexts/BreadcrumbContext'
import { quotesApi } from '@/services/quotes'
import { clientsApi } from '@/services/clients'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { QuoteGrandTotalSummary } from '@/components/billing/QuoteGrandTotalSummary'
import {
  computeDocumentTotals,
  type TradeDiscountInput,
} from '@/lib/documentTotals'
import { formatCurrency } from '@/lib/format'
import { computeLineTotal } from '@/lib/lineItems'
import { cn } from '@/lib/utils'
import { defaultQuoteValidUntil } from '@/lib/quotes'
import { QUOTE_STATUS, type QuoteStatus } from '@/types'
import type {
  Client,
  LineItem,
  CreateQuoteRequest,
  Surcharge,
  ServiceItem,
} from '@/types'

interface Props {
  mode: 'create' | 'edit'
}

// 2.5.4 Edit-quote Status dropdown: the canonical 4-status Quote set.
const EDIT_STATUS_OPTIONS: QuoteStatus[] = [
  QUOTE_STATUS.DRAFT,
  QUOTE_STATUS.SENT,
  QUOTE_STATUS.WON,
  QUOTE_STATUS.LOST,
]

const PAGE_CARD_CLASS =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_4px_4px_-4px_rgba(38,45,66,0.05),0_16px_32px_-4px_rgba(38,45,66,0.1)]'

const OUTLINE_BUTTON_CLASS =
  'h-10 gap-2 rounded-xl border border-[#e7e9ef] bg-white p-3 text-sm font-medium normal-case leading-none text-[#171a20] hover:bg-[#fbfbfc]'

const PRIMARY_BUTTON_CLASS =
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] p-3 text-sm font-medium normal-case leading-none text-white hover:bg-[#e6511c]'

const FIELD_CLASS = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_CLASS
)

const PICKER_FIELD_CLASS = cn(FIELD_CLASS, FORM_CONTROL_SELECT_OPEN_CLASS)

const STATUS_STYLES: Record<QuoteStatus, { bg: string; text: string }> = {
  draft: { bg: '#f5f6fa', text: '#171a20' },
  sent: { bg: '#dbeafe', text: '#1d4ed8' },
  won: { bg: '#d1fae5', text: '#065f46' },
  lost: { bg: '#fde9e8', text: '#991b1b' },
}

function SurfaceCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn(PAGE_CARD_CLASS, className)}>{children}</section>
  )
}

function SectionHeader({
  title,
  step,
  icon,
  uppercase = false,
}: {
  title: string
  step?: number
  icon?: ReactNode
  uppercase?: boolean
}) {
  return (
    <div className='flex items-center gap-3 px-6 pb-2 pt-6'>
      {typeof step === 'number' ? (
        <span className='flex size-6 shrink-0 items-center justify-center rounded-md bg-[#ff5a1f] font-sans text-xs font-medium leading-6 text-white'>
          {step}
        </span>
      ) : (
        <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fff1ea] text-[#ff5a1f]'>
          {icon}
        </span>
      )}
      <h2
        className={cn(
          'font-heading text-sm font-semibold leading-[1.2] text-[#171a20]',
          uppercase ? 'uppercase' : 'normal-case'
        )}
      >
        {title}
      </h2>
    </div>
  )
}

function ToolbarDateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <DatePicker
      id={id}
      value={value}
      onChange={onChange}
      variant='field'
      containerClassName='w-auto'
      errorPlacement='right'
      openOnFieldClick
      leadingContent={
        <>
          <Calendar className='size-4 shrink-0 text-[#272c36]' />
          <span className='select-none whitespace-nowrap text-sm font-medium normal-case leading-[1.4] text-[#6c7285]'>
            {label}:
          </span>
        </>
      }
      trailingContent={
        <ChevronDown
          aria-hidden='true'
          className='size-4 shrink-0 text-[#171a20]'
        />
      }
      className={cn(
        'h-10 w-auto shrink-0 cursor-pointer gap-2 rounded-xl border border-[#e7e9ef] bg-white py-3 pl-4 pr-3 shadow-none [&>button]:hidden [&>input]:w-[80px] [&>input]:shrink-0 [&>input]:cursor-text [&>input]:bg-transparent [&>input]:p-0 [&>input]:text-sm [&>input]:font-medium [&>input]:normal-case [&>input]:leading-[1.4] [&>input]:text-[#171a20] [&>input]:placeholder:text-[#171a20]',
        FORM_CONTROL_FOCUS_WITHIN_CLASS
      )}
    />
  )
}

function StatusBadge({ status }: { status: QuoteStatus }) {
  const { t } = useTranslation('quotes')
  const tone = STATUS_STYLES[status]

  return (
    <span
      className='inline-flex h-8 items-center justify-center rounded-lg px-2 text-sm font-medium leading-[1.4] capitalize'
      style={{
        backgroundColor: tone.bg,
        color: tone.text,
      }}
    >
      {t(`list.status.${status}`)}
    </span>
  )
}

function lineFromServiceItem(item: ServiceItem): LineItem {
  const base = {
    service_item_id: item.id,
    description: item.name,
    service_description: item.description,
    image_file_id: item.image_file_id ?? null,
    quantity: 1,
    unit_price: item.unit_price,
    tax_rate: item.tax_rate ?? 20,
    image_url: item.image_url ?? null,
  }

  return {
    ...base,
    line_total: computeLineTotal(base),
  }
}

// 2.5.2 Create quote / 2.5.4 Edit quote - line items, totals/tax, client picker.
export default function QuoteFormPage({ mode }: Props) {
  const { t } = useTranslation(['quotes', 'enums', 'shell'])
  useActiveServiceCategories()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const preselectClient = searchParams.get('client_id') ?? undefined
  // 2.3.18 post-inspection quote: links the created quote back to the job it
  // was raised from (carried through as a query param from the job detail).
  const jobId = searchParams.get('job_id') ?? undefined
  // 2.3.19 "Quotation to be created on-site": after saving the quote, return to
  // the source intervention detail so the next Visit Quote action is shown.
  const collectJob = searchParams.get('collect_job') ?? undefined

  const { data: detail, isLoading } = useQuote(mode === 'edit' ? id : undefined)
  const { data: optionalFeeTemplates } = useQuoteOptionalFeeTemplates()
  const create = useCreateQuote()
  const update = useUpdateQuote(id ?? '')

  const [clientId, setClientId] = useState<string>(preselectClient ?? '')
  const [title, setTitle] = useState('')
  const [validUntil, setValidUntil] = useState(() =>
    mode === 'create' ? defaultQuoteValidUntil() : ''
  )
  const [items, setItems] = useState<LineItem[]>([])
  const [surcharges, setSurcharges] = useState<OptionalCharge[]>([])
  const [tradeDiscount, setTradeDiscount] = useState<TradeDiscountInput | null>(
    null
  )
  const [notes, setNotes] = useState('')
  const [requestSignature, setRequestSignature] = useState(false)
  const [allowOnlinePayment, setAllowOnlinePayment] = useState(false)
  const [libraryPageOpen, setLibraryPageOpen] = useState(false)
  const [serviceDrawerOpen, setServiceDrawerOpen] = useState(false)
  const [optionalFeeDrawerOpen, setOptionalFeeDrawerOpen] = useState(false)
  const [editingOptionalFeeIndex, setEditingOptionalFeeIndex] = useState<
    number | null
  >(null)
  // 2.5.4: manual quote status (edit mode). Hydrated from the fetched quote.
  const [status, setStatus] = useState<QuoteStatus | ''>('')
  const [errors, setErrors] = useState<{
    client_id?: string
    items?: string
    surcharges?: string
  }>({})
  const [saving, setSaving] = useState(false)
  // Create-mode attachments: collected locally, flushed after the quote exists.
  const pendingAttachments = useRef<PendingAttachFilesHandle>(null)
  const tradeDiscountEditorRef = useRef<HTMLDivElement | null>(null)

  useBreadcrumbLabel(
    mode === 'edit' ? (detail?.data?.number ?? undefined) : null
  )

  useEffect(() => {
    if (mode === 'edit' && detail?.data) {
      const q = detail.data
      // Hydrating local form fields from the fetched quote (one-shot external sync).
      /* eslint-disable react-hooks/set-state-in-effect */
      setClientId(q.client_id)
      setTitle(q.title ?? '')
      setValidUntil(q.valid_until ?? '')
      setItems(q.items?.length ? q.items : [])
      setSurcharges(
        (q.surcharges ?? []).map(s => ({
          id: s.id,
          type: s.type,
          description: s.description ?? '',
          image_file_id: s.image_file_id ?? null,
          image_url: s.image_url ?? null,
          amount: s.amount,
          tax_rate: s.tax_rate,
        }))
      )
      setTradeDiscount(
        q.discount_type && (q.discount_value ?? 0) > 0
          ? {
              type: q.discount_type,
              value: q.discount_value,
            }
          : null
      )
      setNotes(q.notes ?? '')
      setStatus(q.status ?? '')
      // Hydrate both flags from the persisted quote (feedback #30).
      setRequestSignature(!!q.request_signature || !!q.signature_request_id)
      setAllowOnlinePayment(!!q.allow_online_payment)
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [mode, detail])

  // Arriving from another screen (e.g. the post-job action modal) keeps the
  // previous scroll offset, landing the form mid-page — start at the top.
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [])

  // Name of the currently selected client, so the picker shows it right away
  // when the preloaded first page does not contain it (pre-selected client from
  // ?client_id= or from the quote being edited).
  const { data: selectedClient } = useQuery({
    queryKey: ['clients', 'detail', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: !!clientId,
  })
  const { data: sourceJobDetail } = useJob(
    mode === 'create' ? jobId : undefined
  )
  const sourceJob = sourceJobDetail?.data
  const sourceInterventionLabel = useMemo(() => {
    if (!sourceJob) return null
    const location = [sourceJob.postal_code, sourceJob.city]
      .filter(Boolean)
      .join(' · ')
    const parts = [location, sourceJob.service_type].filter(Boolean)
    return (
      parts.join(' · ') || sourceJob.reference || sourceJob.client?.name || null
    )
  }, [sourceJob])

  const surchargeTotal = useMemo(
    () => computeChargesTotal(surcharges),
    [surcharges]
  )
  const documentTotals = useMemo(
    () => computeDocumentTotals(items, surcharges, tradeDiscount),
    [items, surcharges, tradeDiscount]
  )
  const grandTotal = documentTotals.total
  const surchargeVatRate = useMemo(() => {
    const rates = Array.from(
      new Set(items.map(item => item.tax_rate).filter(rate => rate > 0))
    ).sort((a, b) => a - b)

    return rates[0] ?? 0
  }, [items])
  const taxBreakdown = useMemo(() => {
    return documentTotals.tax_breakdown
      .filter(row => row.tax > 0)
      .map(row => ({
        label: t('form.grandTotal.vatRateLabel', { rate: row.rate }),
        amount: row.tax,
      }))
  }, [documentTotals.tax_breakdown, t])
  const hasTradeDiscountRow =
    !!tradeDiscount?.type && (tradeDiscount.value ?? 0) > 0
  const currentStatus =
    mode === 'edit'
      ? status || detail?.data?.status || QUOTE_STATUS.DRAFT
      : QUOTE_STATUS.DRAFT

  const validate = () => {
    const next: typeof errors = {}
    if (!clientId) next.client_id = t('form.validation.clientRequired')
    // A line item without a description (e.g. no service picked) is rejected by
    // the API — catch it here and point at the offending line instead of letting
    // the raw "items.N.description" 422 surface as a toast.
    const incompleteIdx = items.findIndex(it => !it.description?.trim())
    if (items.length === 0) next.items = t('form.validation.itemsRequired')
    else if (incompleteIdx >= 0)
      next.items = t('form.validation.itemIncomplete', {
        line: incompleteIdx + 1,
      })
    // A custom surcharge requires a description (matches the backend rule).
    if (surcharges.some(s => s.type === 'custom' && !s.description?.trim()))
      next.surcharges = t('form.validation.surchargeDescriptionRequired')
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildBody = (
    nextStatus: QuoteStatus | undefined
  ): CreateQuoteRequest => ({
    client_id: clientId,
    title: title.trim() || null,
    // Only sent on create; edit keeps the original linkage server-side.
    job_id: mode === 'create' ? jobId : undefined,
    valid_until: validUntil || undefined,
    items: items.map(
      ({
        id,
        service_item_id,
        description,
        service_description,
        image_file_id,
        quantity,
        unit_price,
        tax_rate,
        line_total,
      }) => ({
        id,
        service_item_id,
        description,
        service_description,
        image_file_id,
        quantity,
        unit_price,
        tax_rate,
        line_total,
      })
    ),
    surcharges: surcharges.map(
      (s): Surcharge => ({
        type: s.type,
        description: s.description || undefined,
        image_file_id: s.image_file_id ?? undefined,
        amount: s.amount,
        tax_rate: s.tax_rate ?? surchargeVatRate,
      })
    ),
    discount_type:
      tradeDiscount && (tradeDiscount.value ?? 0) > 0
        ? (tradeDiscount.type ?? 'fixed')
        : null,
    discount_value:
      tradeDiscount && (tradeDiscount.value ?? 0) > 0
        ? (tradeDiscount.value ?? 0)
        : 0,
    notes: notes || undefined,
    status: nextStatus,
    request_signature: requestSignature,
    // Feedback #30: previously dropped here, so the flag never persisted.
    allow_online_payment: allowOnlinePayment,
  })

  // `send` routes to the quote detail with ?send=1, which opens the "Send quote
  // to client" dialog (2.5.6). Sending itself is POST /quotes/:id/send — the
  // form never sets the `sent` status itself, on create or on edit.
  const save = async (nextStatus: QuoteStatus | undefined, send = false) => {
    if (!validate()) return
    setSaving(true)
    try {
      if (mode === 'edit' && id) {
        await update.mutateAsync(buildBody(nextStatus))
        if (pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(id)
          } catch {
            toast({ variant: 'destructive', title: t('attachments.error') })
          }
        }
        if (send) {
          navigate(`/quotes/${id}?send=1`)
        } else {
          toast({ variant: 'success', title: t('form.saveSuccess') })
          navigate(`/quotes/${id}`)
        }
      } else {
        const res = await create.mutateAsync(buildBody(nextStatus))
        const newId = res.data?.id
        // Persist any files picked during create now that the quote exists.
        if (newId && pendingAttachments.current?.count()) {
          try {
            await pendingAttachments.current.flush(newId)
          } catch {
            toast({ variant: 'destructive', title: t('attachments.error') })
          }
        }
        if (send && newId) {
          // Save and send -> route to the send dialog on the detail screen (2.5.6).
          navigate(`/quotes/${newId}?send=1`)
        } else if (collectJob && newId) {
          // Site Visit / Visit Quote draft saves return to the intervention
          // detail so the next Visit Quote action is shown.
          toast({ variant: 'success', title: t('form.saveSuccess') })
          navigate(`/calendar?job=${collectJob}`)
        } else {
          toast({ variant: 'success', title: t('form.saveSuccess') })
          if (newId) navigate(`/quotes/${newId}`)
        }
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('form.saveError')),
      })
    } finally {
      setSaving(false)
    }
  }

  const appendLineItems = (newLines: LineItem[]) => {
    if (newLines.length === 0) return
    setItems(current => {
      const isPristine =
        current.length === 1 &&
        !current[0].description &&
        !current[0].service_item_id &&
        current[0].unit_price === undefined
      return isPristine ? newLines : [...current, ...newLines]
    })
    setErrors(current => ({ ...current, items: undefined }))
  }

  const appendFromLibrary = (picked: SelectedServiceItem[]) => {
    if (picked.length === 0) return

    const newLines = picked.map(({ item, quantity }) => {
      const line = lineFromServiceItem(item)
      return {
        ...line,
        quantity,
        line_total: computeLineTotal({
          quantity,
          unit_price: line.unit_price,
          tax_rate: line.tax_rate,
        }),
      }
    })
    appendLineItems(newLines)
    setLibraryPageOpen(false)
  }

  const appendManualService = (item: LineItem) => {
    appendLineItems([item])
  }

  const appendOptionalFee = (charge: OptionalCharge) => {
    setSurcharges(current => [...current, charge])
    setErrors(current => ({ ...current, surcharges: undefined }))
  }

  const updateOptionalFee = (charge: OptionalCharge) => {
    setSurcharges(current =>
      current.map((item, index) =>
        index === editingOptionalFeeIndex ? charge : item
      )
    )
    setErrors(current => ({ ...current, surcharges: undefined }))
  }

  const preview = async () => {
    if (mode === 'edit' && id) {
      try {
        const res = await quotesApi.pdf(id)
        if (res.data?.url) window.open(res.data.url, '_blank', 'noopener')
      } catch (e) {
        toast({
          variant: 'destructive',
          title: getApiErrorMessage(e, t('form.previewError')),
        })
      }
    } else {
      toast({ variant: 'default', title: t('form.saveFirst') })
    }
  }

  if (mode === 'edit' && isLoading) {
    return (
      <div className='py-10 text-center text-sm font-normal normal-case text-[#6c7285]'>
        {t('form.loading')}
      </div>
    )
  }

  const pageTitle =
    mode === 'edit' ? t('form.editTitle') : t('form.createTitle')
  const sendButtonLabel = t('form.sendToClient')
  const saveStatus = mode === 'edit' ? status || undefined : QUOTE_STATUS.DRAFT
  const optionalFeeQuickPresets =
    optionalFeeTemplates?.data?.map(template => ({
      id: template.id,
      type: template.type,
      label: template.name,
      amount: template.amount,
      description: template.description,
      image_file_id: template.image_file_id ?? null,
      image_url: optionalFeeImageUrl(template),
    })) ?? []
  const editingOptionalFee =
    editingOptionalFeeIndex === null
      ? null
      : (surcharges[editingOptionalFeeIndex] ?? null)
  const scrollToTradeDiscountEditor = () => {
    tradeDiscountEditorRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }

  return (
    <div className='-mx-6 -mt-6 flex min-h-full flex-col bg-[#f4f5f8]'>
      <h1 className='sr-only'>{pageTitle}</h1>

      <header className='sticky top-0 z-20 box-border h-[72px] border-b border-[#e7e9ef] bg-white shadow-[0_1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex h-full flex-wrap items-center gap-6 px-16'>
          <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3'>
            <div className='shrink-0'>
              {mode === 'edit' ? (
                <Select
                  value={currentStatus}
                  onValueChange={value => setStatus(value as QuoteStatus)}
                >
                  <SelectTrigger
                    className={cn(
                      'h-8 min-w-[108px] rounded-lg border-0 bg-[#f5f6fa] px-2 text-sm font-medium normal-case leading-[1.4] text-[#171a20] shadow-none',
                      FORM_CONTROL_SELECT_OPEN_CLASS
                    )}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='rounded-xl border-[#e7e9ef] p-2'>
                    {EDIT_STATUS_OPTIONS.map(opt => (
                      <SelectItem
                        key={opt}
                        value={opt}
                        className='rounded-lg py-2 text-sm font-normal normal-case text-[#171a20]'
                      >
                        {t(`enums:quoteStatus.${opt}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <StatusBadge status={QUOTE_STATUS.DRAFT} />
              )}
            </div>

            <ToolbarDateField
              id='quote-valid-until'
              label={t('form.validUntil')}
              value={validUntil}
              onChange={setValidUntil}
            />
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <Button
              type='button'
              variant='outline'
              className={OUTLINE_BUTTON_CLASS}
              onClick={() => navigate(-1)}
            >
              {t('form.cancel')}
            </Button>
            <Button
              type='button'
              variant='outline'
              className={OUTLINE_BUTTON_CLASS}
              onClick={preview}
            >
              <Play className='size-4' />
              {t('form.previewQuote')}
            </Button>
            <Button
              type='button'
              variant='outline'
              className={OUTLINE_BUTTON_CLASS}
              disabled={saving}
              onClick={() => void save(saveStatus)}
            >
              <Save className='size-4' />
              {t('form.save')}
            </Button>
            <Button
              type='button'
              className={PRIMARY_BUTTON_CLASS}
              disabled={saving}
              onClick={() =>
                void save(
                  mode === 'create' ? QUOTE_STATUS.DRAFT : undefined,
                  true
                )
              }
            >
              <Send className='size-4' />
              {sendButtonLabel}
            </Button>
          </div>
        </div>
      </header>

      <div className='flex-1 bg-[#fafafa] px-16 py-6'>
        <div className='flex flex-col gap-6'>
          <SurfaceCard>
            <SectionHeader title={t('form.clientDetails')} step={1} />
            <div className='space-y-4 px-6 pb-6 pt-2'>
              <div className='grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end'>
                <div className='min-w-0 space-y-1'>
                  <Label className='text-xs font-medium normal-case leading-4 text-[#171a20]'>
                    {t('form.client')}{' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <EntityPicker<Client>
                    queryKey='clients'
                    preload
                    placeholder={t('form.searchClients')}
                    fetcher={({ search, limit, page }) =>
                      clientsApi.list({
                        search,
                        limit,
                        page,
                        sort_column: 'name',
                        sort_order: 'asc',
                      })
                    }
                    getOptionLabel={c => c.name}
                    getOptionValue={c => c.id}
                    renderOption={c => (
                      <ClientNameWithProspect
                        name={c.name}
                        category={c.category}
                      />
                    )}
                    renderValue={(label, item) => (
                      <ClientNameWithProspect
                        name={label}
                        category={
                          item?.category ?? selectedClient?.data?.category
                        }
                      />
                    )}
                    initialLabel={selectedClient?.data?.name}
                    value={clientId || null}
                    onChange={value => {
                      setClientId(value)
                      setErrors(current => ({
                        ...current,
                        client_id: undefined,
                      }))
                    }}
                    variant='search'
                    className={cn(
                      PICKER_FIELD_CLASS,
                      'hover:bg-[#eef0f5]',
                      errors.client_id && 'ring-1 ring-destructive'
                    )}
                  />
                  {errors.client_id && (
                    <p className='text-xs text-destructive'>
                      {errors.client_id}
                    </p>
                  )}
                </div>

                <Permit module='clients' action='edit'>
                  <button
                    type='button'
                    className='inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border border-dashed border-[#ff8b66] px-4 text-sm font-medium normal-case leading-none text-[#ff5a1f] transition-colors hover:bg-[#fff1ea]'
                    onClick={() => navigate('/clients/new')}
                  >
                    <Plus className='size-4' />
                    {t('form.createNewClient')}
                  </button>
                </Permit>
              </div>

              {sourceInterventionLabel && (
                <div className='rounded-xl bg-[#fff1ea] px-4 py-3 text-sm font-medium normal-case leading-[1.4] text-[#ff5a1f]'>
                  {t('form.createdFromIntervention', {
                    intervention: sourceInterventionLabel,
                  })}
                </div>
              )}

              <div className='space-y-1'>
                <Label
                  htmlFor='quote-title'
                  className='text-xs font-medium normal-case leading-4 text-[#171a20]'
                >
                  {t('form.title')}
                </Label>
                <Input
                  id='quote-title'
                  value={title}
                  maxLength={150}
                  className={FIELD_CLASS}
                  onChange={event => setTitle(event.target.value)}
                  placeholder={t('form.titlePlaceholder')}
                />
              </div>
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader title={t('form.lineItems')} step={2} />
            <div className='px-6 pb-6 pt-2'>
              <LineItemsEditor
                items={items}
                onChange={setItems}
                error={errors.items}
                required
                hideHeading
                hideTotals
                fractionDigits={2}
                variant='quote'
                onLibraryClick={() => setLibraryPageOpen(true)}
                onAddItemClick={() => setServiceDrawerOpen(true)}
                tradeDiscountRow={
                  hasTradeDiscountRow && tradeDiscount
                    ? {
                        title: t('form.tradeDiscountCard.rowTitle'),
                        description: t('form.tradeDiscountCard.rowDescription'),
                        amount: documentTotals.discount_amount,
                        actionLabel: t('form.tradeDiscountCard.modify'),
                        onAction: scrollToTradeDiscountEditor,
                      }
                    : null
                }
              />
            </div>
          </SurfaceCard>

          <SurfaceCard>
            <SectionHeader title={t('form.optionalCharges')} step={3} />
            <div className='px-6 pb-4 pt-2'>
              <OptionalChargesSection
                value={surcharges}
                onChange={setSurcharges}
                error={errors.surcharges}
                hideHeading
                fractionDigits={2}
                vatRate={surchargeVatRate}
                onAddChargeClick={() => {
                  setEditingOptionalFeeIndex(null)
                  setOptionalFeeDrawerOpen(true)
                }}
                quickPresets={optionalFeeQuickPresets}
              />
            </div>
            <QuoteGrandTotalSummary
              subtotal={documentTotals.subtotal}
              total={documentTotals.total}
              surchargeTotal={surchargeTotal}
              grandTotal={documentTotals.total}
              tradeDiscountContent={
                tradeDiscount ? (
                  <div ref={tradeDiscountEditorRef}>
                    <TradeDiscountEditor
                      value={tradeDiscount}
                      amount={documentTotals.discount_amount}
                      onChange={setTradeDiscount}
                      onRemove={() => setTradeDiscount(null)}
                    />
                  </div>
                ) : undefined
              }
              tradeDiscountAmount={documentTotals.discount_amount}
              tradeDiscountRowLabel={t('form.tradeDiscountCard.rowTitle')}
              tradeDiscountLabel={
                tradeDiscount ? undefined : t('form.tradeDiscount')
              }
              onTradeDiscountClick={() =>
                setTradeDiscount({ type: 'fixed', value: 0 })
              }
              taxBreakdown={taxBreakdown}
            />
          </SurfaceCard>

          <div className='grid gap-6 xl:grid-cols-2'>
            <SurfaceCard className='flex min-h-[238px] flex-col'>
              <SectionHeader title={t('form.notes')} step={4} />
              <div className='flex min-h-0 flex-1 px-6 pb-6 pt-2'>
                <Textarea
                  id='notes'
                  value={notes}
                  maxLength={2000}
                  rows={6}
                  className={cn(
                    FIELD_CLASS,
                    'h-full min-h-[150px] flex-1 resize-none overflow-y-auto rounded-xl'
                  )}
                  onChange={e => setNotes(e.target.value)}
                  placeholder={t('form.notesPlaceholder')}
                />
              </div>
            </SurfaceCard>

            <SurfaceCard className='flex min-h-[238px] flex-col'>
              <SectionHeader title={t('form.attachFiles')} step={5} />
              <div className='min-h-0 flex-1 px-6 pb-6 pt-2'>
                <PendingAttachFiles
                  attachTo='quote'
                  ref={pendingAttachments}
                  entityId={mode === 'edit' ? id : undefined}
                  clientId={clientId || undefined}
                  uploadFn={quotesApi.uploadDocument}
                  attachExistingFn={quotesApi.attachExistingDocument}
                  listExistingFn={quotesApi.listDocuments}
                  removeFn={quotesApi.removeDocument}
                  renameFn={quotesApi.renameDocument}
                  hideHeading
                  brandTabs
                />
              </div>
            </SurfaceCard>
          </div>
        </div>
      </div>

      <footer className='sticky bottom-0 z-20 mt-auto -mb-6 box-border h-[72px] border-t border-[#e7e9ef] bg-white shadow-[0_-1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex h-full flex-col gap-4 px-16 sm:flex-row sm:items-center sm:justify-between'>
          <div className='min-w-0'>
            <p className='text-xs font-medium leading-[1.4] text-[#6c7285]'>
              {t('form.totalInclVat')}
            </p>
            <p className='font-heading text-base font-semibold leading-[1.2] tracking-[-0.48px] text-[#171a20]'>
              {formatCurrency(grandTotal, 'EUR', 2)}
            </p>
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <Button
              type='button'
              className={PRIMARY_BUTTON_CLASS}
              disabled={saving}
              onClick={() =>
                void save(
                  mode === 'create' ? QUOTE_STATUS.DRAFT : undefined,
                  true
                )
              }
            >
              <Send className='size-4' />
              {sendButtonLabel}
            </Button>
          </div>
        </div>
      </footer>

      {libraryPageOpen && (
        <ServiceItemLibraryPage
          onClose={() => setLibraryPageOpen(false)}
          onPick={appendFromLibrary}
        />
      )}

      <AddQuoteServiceDrawer
        open={serviceDrawerOpen}
        onOpenChange={setServiceDrawerOpen}
        onAdd={appendManualService}
      />

      <OptionalFeeDocumentDialog
        key={`${optionalFeeDrawerOpen ? 'open' : 'closed'}:${editingOptionalFee?.id ?? editingOptionalFeeIndex ?? 'new'}`}
        open={optionalFeeDrawerOpen}
        onOpenChange={open => {
          setOptionalFeeDrawerOpen(open)
          if (!open) setEditingOptionalFeeIndex(null)
        }}
        onAdd={appendOptionalFee}
        onUpdate={updateOptionalFee}
        initialCharge={editingOptionalFee}
        defaultVatRate={surchargeVatRate}
      />
    </div>
  )
}
